/*
 * CD Peacock + ShapeDiver cart bridge — v8 (redirect-only)
 *
 * Required behavior:
 *   ShapeDiver custom jewellery Add to Cart succeeds
 *     -> redirect directly to Shopify /cart
 *
 * Why this version is different:
 *   - Does NOT force-close or hide the ShapeDiver popup.
 *   - Does NOT open the cart drawer.
 *   - Does NOT depend only on ShapeDiver's redirect setting/callback.
 *   - While the ShapeDiver configurator is visible, it watches Shopify /cart.js.
 *     As soon as cart quantity increases, it redirects to /cart.
 *   - Existing ShapeDiver callback/postMessage hooks remain as fast-path triggers.
 */
(function () {
  'use strict';

  var pollTimer = null;
  var visibilityTimer = null;
  var baseline = null;
  var configuratorWasOpen = false;
  var redirecting = false;
  var snapshotInFlight = false;
  var lastSnapshotAt = 0;

  function rootUrl() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function cartUrl() {
    return rootUrl() + 'cart';
  }

  function cartJsonUrl() {
    return rootUrl() + 'cart.js';
  }

  function visible(el) {
    if (!el || !el.isConnected) return false;
    try {
      var style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
      var rect = el.getBoundingClientRect();
      return rect.width > 40 && rect.height > 40;
    } catch (e) {
      return false;
    }
  }

  function isShapeDiverFrame(iframe) {
    var src = '';
    try { src = String(iframe.src || iframe.getAttribute('src') || '').toLowerCase(); } catch (e) {}
    return (
      src.indexOf('shapediver') !== -1 ||
      src.indexOf('appbuilder') !== -1 ||
      src.indexOf('app-builder') !== -1
    );
  }

  function configuratorIsOpen() {
    var frames = document.querySelectorAll('iframe');
    for (var i = 0; i < frames.length; i += 1) {
      if (isShapeDiverFrame(frames[i]) && visible(frames[i])) return true;
    }

    var selectors = [
      '[id*="shapediver"]',
      '[class*="shapediver"]',
      '[id*="app-builder"]',
      '[class*="app-builder"]'
    ];

    for (var s = 0; s < selectors.length; s += 1) {
      var nodes = document.querySelectorAll(selectors[s]);
      for (var n = 0; n < nodes.length; n += 1) {
        if (visible(nodes[n])) {
          var rect = nodes[n].getBoundingClientRect();
          if (rect.width >= window.innerWidth * 0.6 && rect.height >= window.innerHeight * 0.55) return true;
        }
      }
    }

    return false;
  }

  function normalizeCart(cart) {
    cart = cart || {};
    var items = Array.isArray(cart.items) ? cart.items : [];
    return {
      itemCount: Number(cart.item_count || 0),
      totalQuantity: items.reduce(function (sum, item) {
        return sum + Number(item && item.quantity || 0);
      }, 0),
      keys: items.map(function (item) { return String(item && item.key || ''); }).sort().join('|')
    };
  }

  function fetchCartSnapshot() {
    if (snapshotInFlight) return Promise.resolve(null);
    snapshotInFlight = true;
    lastSnapshotAt = Date.now();

    return fetch(cartJsonUrl(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart.js request failed: ' + response.status);
        return response.json();
      })
      .then(normalizeCart)
      .catch(function (error) {
        console.warn('[CDP ShapeDiver v8] cart snapshot failed', error);
        return null;
      })
      .then(function (snapshot) {
        snapshotInFlight = false;
        return snapshot;
      });
  }

  function redirectToCart(reason) {
    if (redirecting) return;
    redirecting = true;
    console.info('[CDP ShapeDiver v8] Add to Cart detected; redirecting to cart (' + reason + ')');
    window.location.assign(cartUrl());
  }

  function cartHasIncreased(next) {
    if (!baseline || !next) return false;
    return next.itemCount > baseline.itemCount || next.totalQuantity > baseline.totalQuantity;
  }

  function pollCart() {
    if (redirecting || !configuratorIsOpen()) return;
    if (Date.now() - lastSnapshotAt < 450) return;

    fetchCartSnapshot().then(function (next) {
      if (!next || redirecting) return;
      if (cartHasIncreased(next)) {
        redirectToCart('cart.js quantity change');
        return;
      }

      /* Keep key changes as a defensive signal only when count/quantity is not lower. */
      if (baseline && next.keys !== baseline.keys && next.itemCount >= baseline.itemCount && next.totalQuantity >= baseline.totalQuantity) {
        redirectToCart('cart.js line change');
      }
    });
  }

  function startPolling() {
    if (pollTimer) return;
    pollTimer = window.setInterval(pollCart, 550);
  }

  function stopPolling() {
    if (!pollTimer) return;
    window.clearInterval(pollTimer);
    pollTimer = null;
  }

  function captureBaselineAndWatch() {
    fetchCartSnapshot().then(function (snapshot) {
      if (snapshot) baseline = snapshot;
      startPolling();
    });
  }

  function watchConfiguratorVisibility() {
    var open = configuratorIsOpen();

    if (open && !configuratorWasOpen) {
      configuratorWasOpen = true;
      baseline = null;
      captureBaselineAndWatch();
    } else if (!open && configuratorWasOpen) {
      configuratorWasOpen = false;
      baseline = null;
      stopPolling();
    }
  }

  /* Fast-path: ShapeDiver historically calls this after a successful cart operation. */
  function rerenderHandler() {
    redirectToCart('shapediver_rerender_cart');
  }
  window.shapediver_rerender_cart = rerenderHandler;

  /* Reclaim the callback if another script overwrites it later. */
  window.setInterval(function () {
    if (window.shapediver_rerender_cart !== rerenderHandler) {
      window.shapediver_rerender_cart = rerenderHandler;
    }
  }, 1000);

  /* Additional fast-path for ShapeDiver/App Builder cross-frame cart-success messages. */
  window.addEventListener('message', function (event) {
    if (redirecting || !configuratorIsOpen()) return;

    var text = '';
    try { text = typeof event.data === 'string' ? event.data : JSON.stringify(event.data); } catch (e) {}
    if (!text) return;

    var normalized = text.toLowerCase();
    var looksLikeSuccess =
      (normalized.indexOf('added') !== -1 && normalized.indexOf('cart') !== -1) ||
      normalized.indexOf('addtocart') !== -1 ||
      normalized.indexOf('add_to_cart') !== -1 ||
      normalized.indexOf('cartline') !== -1;

    if (looksLikeSuccess) {
      /* Verify Shopify cart once before redirecting whenever possible. */
      fetchCartSnapshot().then(function (next) {
        if (next && baseline && cartHasIncreased(next)) redirectToCart('ShapeDiver message + cart verification');
        else if (next && !baseline) redirectToCart('ShapeDiver cart-success message');
        else window.setTimeout(pollCart, 120);
      });
    }
  }, false);

  function boot() {
    watchConfiguratorVisibility();
    visibilityTimer = window.setInterval(watchConfiguratorVisibility, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
